import { CircularProgress, Stack, Typography } from '@mui/material';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import type { FirebaseServices } from '../../lib/firebase/client';
import { initFirebaseClient } from '../../lib/firebase/client';

const FirebaseContext = createContext<FirebaseServices | null>(null);

const FullscreenState = ({ label }: { readonly label: string }) => (
  <Stack alignItems="center" minHeight="100vh" justifyContent="center" spacing={2}>
    <CircularProgress />
    <Typography variant="body1" color="text.secondary">
      {label}
    </Typography>
  </Stack>
);

const FirebaseProvider = ({ children }: { readonly children: ReactNode }) => {
  const [services, setServices] = useState<FirebaseServices | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    initFirebaseClient()
      .then(initializedServices => {
        setServices(initializedServices);
      })
      .catch(initializationError => {
        setError(
          initializationError instanceof Error
            ? initializationError
            : new Error('Unknown Firebase initialization error'),
        );
      });
  }, []);

  const value = useMemo(() => services, [services]);

  if (error) {
    return (
      <Stack alignItems="center" minHeight="100vh" justifyContent="center" spacing={2}>
        <Typography color="error" variant="h6">
          Unable to connect to Firebase services
        </Typography>
        <Typography color="text.secondary" variant="body2">
          {error.message}
        </Typography>
      </Stack>
    );
  }

  if (!value) {
    return <FullscreenState label="Connecting to Firebase" />;
  }

  return <FirebaseContext.Provider value={value}>{children}</FirebaseContext.Provider>;
};

export const useFirebaseServices = (): FirebaseServices => {
  const context = useContext(FirebaseContext);

  if (!context) {
    throw new Error('useFirebaseServices must be used inside FirebaseProvider');
  }

  return context;
};

export default FirebaseProvider;
